import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Stack, Typography, Divider, TextField, CircularProgress } from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { PALETTE, ApprovalRow, studentName, criteriaHeadline } from "../types";
import MasteryProgression from "./MasteryProgression";

type Props = {
  row: ApprovalRow | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  submitting?: boolean;
};

const RejectMasteryDialog: React.FC<Props> = ({ row, open, onClose, onConfirm, submitting }) => {
  const [notes, setNotes] = useState("");
  const [touched, setTouched] = useState(false);
  const trimmed = notes.trim();
  const invalid = trimmed.length === 0;
  const close = () => { if (submitting) return; setNotes(""); setTouched(false); onClose(); };
  if (!row) return null;

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: PALETTE.ink }}>Reject Mastery Progression</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Row label="Student" value={studentName(row)} />
          <Row label="Program" value={row.programName || "—"} />
          <Row label="Target" value={row.targetName || `Target #${row.targetId}`} />
          <Divider sx={{ my: 0.5, borderColor: PALETTE.border }} />
          <Row label="Progression" value={<MasteryProgression row={row} compact />} />
          <Row label="Criteria" value={criteriaHeadline(row.criteria)} />
        </Stack>
        <DialogContentText sx={{ fontSize: 13, color: PALETTE.slate }}>
          Rejecting sends this target back for more data. A reason is required for the audit trail.
        </DialogContentText>
        <TextField
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => setTouched(true)}
          label="Reason for rejection"
          placeholder="e.g. Additional sessions required before progression"
          required
          fullWidth multiline minRows={3}
          error={touched && invalid}
          helperText={touched && invalid ? "Please provide a reason." : " "}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={submitting} sx={{ textTransform: "none", fontWeight: 700, color: PALETTE.slate }}>Cancel</Button>
        <Button
          onClick={() => { if (invalid) { setTouched(true); return; } onConfirm(trimmed); }}
          disabled={submitting || invalid}
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CancelOutlinedIcon />}
          sx={{ textTransform: "none", fontWeight: 800, bgcolor: PALETTE.rejected.accent, color: "#fff", "&:hover": { bgcolor: PALETTE.rejected.fg } }}
        >
          {submitting ? "Rejecting…" : "Reject Progression"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Stack direction="row" spacing={1.5} alignItems="baseline">
    <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: PALETTE.slate, minWidth: 100 }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: 13.5, fontWeight: 700, color: PALETTE.ink, flex: 1 }}>{value}</Typography>
  </Stack>
);

export default RejectMasteryDialog;
